import { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

/**
 * FirstRunCard — dismissible first-run welcome/guidance card (ADR 009 follow-up).
 *
 * A brand-new resident or property manager lands on a live dashboard with no
 * nudge about what to do first. This card points them at the one or two most
 * useful actions, then remembers it has been seen so it never reappears.
 *
 * Persistence mirrors the `TenantOnboardingWizard` pattern: a per-user
 * `localStorage` Seen_Flag keyed by `storageKey` (caller scopes it by surface +
 * userId). Storage failures are swallowed and treated as "already seen" so the
 * user is never trapped behind an unclosable card (private browsing, no DOM).
 *
 * Presentational only — the caller passes already-`t()`-resolved copy so the
 * component stays translatable without owning any i18n itself. It is a
 * non-blocking card, never a modal gate (Requirement 1.4 / 2.3).
 */

export interface FirstRunCardAction {
  label: string;
  /** Navigate to a route. Renders the action as a router Link. */
  to?: string;
  /** Click handler. Renders the action as a button. Ignored when `to` is set. */
  onClick?: () => void;
}

interface FirstRunCardProps {
  /** Per-user, per-surface localStorage key for the Seen_Flag. */
  storageKey: string;
  title: string;
  description?: string;
  /** 1–3 quick links pointing at the most useful first actions. */
  actions: FirstRunCardAction[];
}

function readSeen(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) === '1';
  } catch {
    // localStorage unavailable (private browsing, no DOM, etc.) — treat as
    // already-seen so we never render a card the user cannot dismiss.
    return true;
  }
}

function markSeen(storageKey: string): void {
  try {
    localStorage.setItem(storageKey, '1');
  } catch {
    /* swallow — benign UI state, not worth surfacing to the user */
  }
}

export function FirstRunCard({ storageKey, title, description, actions }: FirstRunCardProps) {
  // Initialise from storage once on mount; if the flag is set (or storage is
  // unavailable) the card never shows.
  const [dismissed, setDismissed] = useState(() => readSeen(storageKey));

  if (dismissed) return null;

  function dismiss() {
    markSeen(storageKey);
    setDismissed(true);
  }

  return (
    <div
      className="card-brand relative overflow-hidden rounded-xl p-4"
      data-slot="first-run-card"
      role="region"
      aria-label={title}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="tap-target absolute right-1.5 top-1.5 rounded-md p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <X className="size-4" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[var(--bg-surface-raised)]">
          <Sparkles className="size-4 text-[var(--text-accent)]" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
          {description && (
            <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">{description}</p>
          )}

          {actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions.map((action) =>
                action.to ? (
                  <Button key={action.label} size="sm" variant="primary" asChild>
                    <Link to={action.to}>{action.label}</Link>
                  </Button>
                ) : (
                  <Button key={action.label} size="sm" variant="secondary" onClick={action.onClick}>
                    {action.label}
                  </Button>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
